import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useStore } from '@stores';
import { buildProgressBarModuleList, getProgressBarStatus } from '@utils';

type Status = 'active' | 'pending' | 'completed';

/**  Custom Stepper - Segments on top, icons below */
export const Stepper = () => {
  const { t } = useTranslation('common');
  const { formStore } = useStore();
  const moduleList = buildProgressBarModuleList(formStore.formConfig);
  const currentStepIndex = moduleList.findIndex(
    (module) => module === formStore.currentModuleName
  );

  const getStepStatus = (module: string, index: number): Status => {
    const isCompleted = getProgressBarStatus(
      formStore.formConfig,
      formStore.currentModuleName!,
      module
    );

    const isActive = index === currentStepIndex;
    const isPast = index < currentStepIndex;

    return isActive
      ? 'active'
      : isCompleted || isPast
        ? 'completed'
        : 'pending';
  };

  return (
    <Box>
      {/* Horizontal Segments Line */}
      <Box
        sx={{
          height: '4px',
          width: '100%',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {moduleList.map((module, index) => {
          const status = getStepStatus(module, index);

          const bgcolor = {
            pending: 'custom.pendingStep',
            active: 'custom.activeStep',
            completed: 'custom.completedStep',
          };

          return (
            <Box
              key={index}
              sx={{
                flex: 1,
                height: '4px',
                bgcolor: bgcolor[status],
                borderRadius: '2px',
                mr: index < moduleList.length - 1 ? '4px' : 0,
              }}
            />
          );
        })}
      </Box>

      {/* Step Icons and Labels Below Segments */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
        }}
      >
        {moduleList.map((module, index) => {
          const status = getStepStatus(module, index);

          const iconBgColor = {
            pending: 'custom.pendingStep',
            active: 'custom.activeStep',
            completed: 'custom.completedStep',
          };

          return (
            <Box
              key={index}
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0.5,
                  width: '50%',
                }}
              >
                {/* Circular Step Icon */}
                <Box
                  sx={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    bgcolor: iconBgColor[status],
                    border: `2px solid ${iconBgColor[status]}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <StepperIcon status={status} />
                </Box>

                {/* Step Label - Beside icon */}
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'custom.headerText',
                    fontWeight: status === 'active' ? 700 : 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('step')} {index + 1}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

function StepperIcon({ status }: { status: Status }) {
  switch (status) {
    case 'pending':
      return (
        <Box
          sx={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: 'white',
          }}
        />
      );

    case 'active':
      return (
        <Box
          sx={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: 'white',
          }}
        />
      );

    case 'completed':
      return (
        <CheckIcon
          sx={{
            fontSize: '12px',
            color: 'white',
            '& path': {
              stroke: 'white',
              strokeWidth: 2,
            },
          }}
        />
      );

    default:
      return <div />;
  }
}
